import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, X, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "push_optin_dismissed";
const supported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone;

function b64ToBytes(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function subscribePush() {
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permissão de notificações negada. Libere nas configurações do navegador.");
  const { data } = await api.get("/push/vapid-key");
  const sub = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(data.key) });
  const j = sub.toJSON();
  await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys });
}

// Convite para ativar push (músicos). Some quando já está ativo ou foi dispensado.
export default function PushOptIn() {
  const [state, setState] = useState("checking");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === "1") return setState("hidden");
    if (!supported()) return setState(isIOS() && !standalone() ? "ios" : "hidden");
    navigator.serviceWorker.getRegistration("/sw.js").then(async (reg) => {
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub && Notification.permission === "granted") {
        const j = sub.toJSON();
        api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys }).catch(() => {});
        setState("hidden");
      } else setState(Notification.permission === "denied" ? "denied" : "ask");
    }).catch(() => setState("ask"));
  }, []);
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setState("hidden"); };
  const enable = async () => {
    setBusy(true);
    try { await subscribePush(); await api.post("/push/test"); toast.success("Notificações ativadas! Você receberá os chamados ⚡ mesmo com o app fechado."); setState("hidden"); }
    catch (e) { toast.error(e.message || apiError(e.response?.data?.detail)); if (Notification.permission === "denied") setState("denied"); }
    finally { setBusy(false); }
  };
  if (["checking", "hidden"].includes(state)) return null;
  const text = state === "ios" ? "No iPhone, toque em Compartilhar › Adicionar à Tela de Início e abra o Mí Chamou por lá para receber chamados ⚡ com o app fechado."
    : state === "denied" ? "As notificações estão bloqueadas neste navegador. Libere nas configurações do site para receber chamados ⚡ com o app fechado."
    : "Receba os chamados ⚡ Em cima da Hora mesmo com o app fechado.";
  return (
    <div role="region" aria-label="Ativar notificações" data-testid="push-optin" className="mb-4 rounded-2xl border border-[#FF4D00]/40 bg-[#FF4D00]/[0.08] p-4 flex items-start gap-3">
      <BellRing className="h-5 w-5 text-[#FF8A3D] shrink-0 mt-0.5" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-100">{text}</p>
        {state === "ask" && (
          <Button onClick={enable} disabled={busy} data-testid="push-optin-enable" className="mt-2 h-11 rounded-xl bg-[#FF4D00] hover:bg-[#E64500] font-semibold">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden="true" />}Ativar notificações
          </Button>
        )}
      </div>
      <button type="button" onClick={dismiss} aria-label="Dispensar aviso de notificações" data-testid="push-optin-dismiss" className="h-11 w-11 -mt-2 -mr-2 rounded-full flex items-center justify-center hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D]"><X className="h-4 w-4" aria-hidden="true" /></button>
    </div>
  );
}
